import { useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';

import { useInsuranceVerificationMutation } from '@/api/hooks/mutations/use-insurance-verification-mutation';
import animationData from '@/assets/lotties/eligibility-loader.json';
import { Lottie } from '@/components/common/lottie-web/Lottie.tsx';
import StepperCard from '@/components/common/StepperCard.tsx';
import { NavigationBar } from '@/components/NavigationBar';
import { CardContent, CardHeader, CardTitle } from '@/components/ui/card.tsx';
import { useSessionStore } from '@/hooks/use-session-store/use-session-store';
import { logger } from '@/lib/logger';
import { getCarrierTypesNeedingInput } from '@/types/insurance-builder-legacy.ts';
import {
  setConfirmedInsurances,
  setUnverifiedInsurances,
  setVerifiedInsurances,
} from '@/types/insurance-builder.type.ts';

export default function InsuranceVerifyPending() {
  const navigate = useNavigate();

  const {
    patientFirstName,
    patientLastName,
    patientDateOfBirth,
    patientGender,
    insuranceZipCode,
    insuranceBuilder,
  } = useSessionStore();

  const verification = useInsuranceVerificationMutation();
  const verifyAndRedirect = () => {
    if (!patientGender) {
      logger.error('Insurance verification failed: patient gender is required');
      navigate({ to: '/1' });
      return;
    }

    verification.mutate(
      {
        patient: {
          firstName: patientFirstName,
          lastName: patientLastName,
          dateOfBirth: patientDateOfBirth,
          gender: patientGender,
          ...(insuranceZipCode && { zipCode: insuranceZipCode }),
        },
        insurances: insuranceBuilder.queuedInsurances,
      },
      {
        onSuccess: ({ verified, unverified }) => {
          const updatedBuilder = setVerifiedInsurances(
            // if we already did confirmation, then this is pass number 2 so
            // just accept the unverified as confirmed and move to finalization
            insuranceBuilder.ranConfirmation
              ? setConfirmedInsurances(insuranceBuilder, ...unverified)
              : setUnverifiedInsurances(insuranceBuilder, ...unverified),
            ...verified
          );
          useSessionStore
            .getState()
            .updateState({ insuranceBuilder: { ...updatedBuilder, ranVerification: true } });

          if (updatedBuilder.ranConfirmation) {
            // second time through, don't go back to input-confirm
            if (updatedBuilder.unprovidedInsurances.length > 0)
              return navigate({ to: '/insurance/follow-up' });
            return navigate({ to: '/insurance/finalize' });
          }

          const missingCarrierTypes = getCarrierTypesNeedingInput(updatedBuilder);
          if (missingCarrierTypes.length > 0)
            return navigate({
              to: `/insurance/input-confirm?carrierType=${missingCarrierTypes[0]}`,
            });

          return navigate({ to: '/insurance/finalize' });
        },
        onError: (error) => {
          logger.error('Failed to verify insurance', error);

          navigate({ to: '/insurance/options' });
        },
      }
    );
  };

  useEffect(function verifyAndRedirectOnMount() {
    verifyAndRedirect();
    // https://github.com/TanStack/query/issues/5341#issuecomment-3381479893
    // this is a workaround to ensure the mutation is executed immediately upon mount
    // oxlint-disable-next-line exhaustive-deps
  }, []);

  const toInsuranceStart = () => navigate({ to: '/insurance/pre-discovery' });

  return (
    <section>
      <NavigationBar onBack={toInsuranceStart} backLabel="Insurance info" stepIndicator="3 of 4" />
      <StepperCard currentStep={3} totalSteps={4}>
        <CardHeader>
          <CardTitle className="text-center">Looking up your benefits...</CardTitle>
        </CardHeader>
        <CardContent
          className="flex flex-col items-center justify-center space-y-8"
          aria-label="Loading..."
          aria-live="polite"
          aria-describedby="insurance-check-loader"
          aria-busy="true"
          role="status"
        >
          <Lottie animationData={animationData} className="aspect-square size-[80px]" />

          <p className="text-center text-base whitespace-pre-line">
            {`Confirming your enrollment with your selected plan(s).\n\nThis may take a minute.`}
          </p>
        </CardContent>
      </StepperCard>
    </section>
  );
}
